import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  Search,
  Download,
  Filter,
  AlertCircle,
  Clock,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { listOrders, type OrderRow } from "@/integrations/supabase/orders";
import { toast } from "sonner";

interface ConsertoPendente {
  id: string;
  numero_pedido: string;
  cliente_nome: string;
  tipo_defeito: string;
  data_entrada: string;
  dias_em_andamento: number;
  status_atual: string;
  proxima_etapa: string;
  data_prova?: string;
  hora_prova?: string;
  nivel_dificuldade: string;
  urgencia: "alta" | "media" | "baixa";
}

const TIPOS_DEFEITO = [
  "costura_rompida",
  "furo",
  "mancha",
  "alongar",
  "encurtar",
  "ajuste_cintura",
  "ajuste_manga",
  "ajuste_gola",
  "remendo",
  "ziper",
  "botao",
  "pintura",
  "oxidacao",
  "desbotado",
  "outro",
];

const NIVEIS_DIFICULDADE = ["simples", "medio", "complexo"];

const STATUS_CORES: Record<string, string> = {
  RECEPÇÃO: "bg-blue-50 border-blue-200 text-blue-700",
  DIAGNÓSTICO: "bg-purple-50 border-purple-200 text-purple-700",
  ORÇAMENTO: "bg-yellow-50 border-yellow-200 text-yellow-700",
  EXECUÇÃO: "bg-orange-50 border-orange-200 text-orange-700",
  PROVA: "bg-green-50 border-green-200 text-green-700",
  AJUSTES: "bg-pink-50 border-pink-200 text-pink-700",
  PRONTO: "bg-emerald-50 border-emerald-200 text-emerald-700",
  ENTREGUE: "bg-gray-50 border-gray-200 text-gray-700",
};

function calcularUrgencia(diasEmAndamento: number): "alta" | "media" | "baixa" {
  if (diasEmAndamento > 14) return "alta";
  if (diasEmAndamento > 7) return "media";
  return "baixa";
}

function formatarTipoDefeito(tipo: string): string {
  const mapa: Record<string, string> = {
    costura_rompida: "Costura Rompida",
    furo: "Furo/Rasgão",
    mancha: "Mancha/Manchado",
    alongar: "Alongar",
    encurtar: "Encurtar",
    ajuste_cintura: "Ajuste Cintura",
    ajuste_manga: "Ajuste Manga",
    ajuste_gola: "Ajuste Gola",
    remendo: "Remendo",
    ziper: "Zíper/Corrente",
    botao: "Botão",
    pintura: "Pintura/Tingimento",
    oxidacao: "Oxidação",
    desbotado: "Desbotado",
    outro: "Outro",
  };
  return mapa[tipo] || tipo;
}

export default function RelatorioConsertos() {
  const navigate = useNavigate();
  const [busca, setBusca] = useState("");
  const [filtroTipo, setFiltroTipo] = useState<string>("todos");
  const [filtroNivel, setFiltroNivel] = useState<string>("todos");
  const [filtroStatus, setFiltroStatus] = useState<string>("todos");
  const [filtroUrgencia, setFiltroUrgencia] = useState<string>("todos");

  const { data: pedidos, isLoading } = useQuery({
    queryKey: ["orders"],
    queryFn: () => listOrders(),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5, // 5 minutos
  });

  // Filtrar apenas consertos/ajustes
  const consertos: ConsertoPendente[] = useMemo(() => {
    if (!pedidos) return [];

    return pedidos
      .filter((p) => p.type === "conserto" || p.type === "ajuste")
      .filter((p) => p.status !== "Entregue" && p.status !== "Cancelado")
      .map((p) => {
        const consertoDados = (p as any).conserto_dados || {};
        const provaDados = (p as any).prova_dados || {};
        const dataEntrada = consertoDados.dataEntrada || p.created_at || new Date().toISOString();
        const dataEntradaObj = new Date(dataEntrada);
        const hoje = new Date();
        const diasEmAndamento = Math.floor(
          (hoje.getTime() - dataEntradaObj.getTime()) / (1000 * 60 * 60 * 24)
        );

        return {
          id: p.id,
          numero_pedido: p.code,
          cliente_nome: p.customer_name,
          tipo_defeito: consertoDados.tipoDefeito || "não especificado",
          data_entrada: dataEntrada.split("T")[0],
          dias_em_andamento: diasEmAndamento,
          status_atual: p.status,
          proxima_etapa: consertoDados.nivelDificuldade ? `${consertoDados.nivelDificuldade.charAt(0).toUpperCase() + consertoDados.nivelDificuldade.slice(1)}` : "não especificado",
          data_prova: provaDados.data_prova,
          hora_prova: provaDados.hora_prova,
          nivel_dificuldade: consertoDados.nivelDificuldade || "simples",
          urgencia: calcularUrgencia(diasEmAndamento),
        };
      })
      .sort((a, b) => b.dias_em_andamento - a.dias_em_andamento); // Mais antigos primeiro
  }, [pedidos]);

  // Aplicar filtros
  const consertosFilterrados = useMemo(() => {
    return consertos.filter((c) => {
      const matchBusca =
        c.numero_pedido.toLowerCase().includes(busca.toLowerCase()) ||
        c.cliente_nome.toLowerCase().includes(busca.toLowerCase());

      const matchTipo =
        filtroTipo === "todos" || c.tipo_defeito === filtroTipo;
      const matchNivel =
        filtroNivel === "todos" || c.nivel_dificuldade === filtroNivel;
      const matchStatus =
        filtroStatus === "todos" || c.status_atual === filtroStatus;
      const matchUrgencia =
        filtroUrgencia === "todos" || c.urgencia === filtroUrgencia;

      return matchBusca && matchTipo && matchNivel && matchStatus && matchUrgencia;
    });
  }, [consertos, busca, filtroTipo, filtroNivel, filtroStatus, filtroUrgencia]);

  const handleExportarPDF = () => {
    toast.info("Exportação em desenvolvimento");
  };

  const abrirConserto = (numeroPedido: string) => {
    navigate(`/pedido/${numeroPedido}`);
  };

  const estatisticas = {
    total: consertos.length,
    urgentes: consertos.filter((c) => c.urgencia === "alta").length,
    comProvaAgendada: consertos.filter((c) => c.data_prova).length,
    emExecucao: consertos.filter((c) => c.status_atual === "Em produção").length,
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6 space-y-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/relatorios")}
          className="text-purple-600 hover:bg-purple-100"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>

        <div className="space-y-2">
          <h1 className="text-3xl font-bold">📋 Consertos e Ajustes Pendentes</h1>
          <p className="text-gray-600">
            Acompanhamento de todos os consertos em andamento
          </p>
        </div>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <div className="space-y-2">
              <p className="text-sm text-blue-600 font-semibold">Total de Consertos</p>
              <p className="text-3xl font-bold text-blue-900">{estatisticas.total}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="space-y-2">
              <p className="text-sm text-red-600 font-semibold">🔴 Urgentes (&gt;14 dias)</p>
              <p className="text-3xl font-bold text-red-900">{estatisticas.urgentes}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="space-y-2">
              <p className="text-sm text-green-600 font-semibold">✅ Prova Agendada</p>
              <p className="text-3xl font-bold text-green-900">{estatisticas.comProvaAgendada}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="pt-6">
            <div className="space-y-2">
              <p className="text-sm text-orange-600 font-semibold">⚙️ Em Execução</p>
              <p className="text-3xl font-bold text-orange-900">{estatisticas.emExecucao}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Busca */}
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-2 block">
                Buscar
              </label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                <Input
                  placeholder="Pedido ou cliente..."
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Tipo de Defeito */}
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-2 block">
                Tipo de Defeito
              </label>
              <Select value={filtroTipo} onValueChange={setFiltroTipo}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  {TIPOS_DEFEITO.map((tipo) => (
                    <SelectItem key={tipo} value={tipo}>
                      {formatarTipoDefeito(tipo)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Nível de Dificuldade */}
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-2 block">
                Dificuldade
              </label>
              <Select value={filtroNivel} onValueChange={setFiltroNivel}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="simples">Simples</SelectItem>
                  <SelectItem value="medio">Médio</SelectItem>
                  <SelectItem value="complexo">Complexo</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Status */}
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-2 block">
                Status
              </label>
              <Select value={filtroStatus} onValueChange={setFiltroStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="Aguardando aprovação">Aguardando aprovação</SelectItem>
                  <SelectItem value="Em produção">Em produção</SelectItem>
                  <SelectItem value="Finalizando">Finalizando</SelectItem>
                  <SelectItem value="Pronto">Pronto</SelectItem>
                  <SelectItem value="Aguardando retirada">Aguardando retirada</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Urgência */}
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-2 block">
                Urgência
              </label>
              <Select value={filtroUrgencia} onValueChange={setFiltroUrgencia}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="alta">🔴 Urgente (&gt;14 dias)</SelectItem>
                  <SelectItem value="media">🟡 Médio (7-14 dias)</SelectItem>
                  <SelectItem value="baixa">🟢 Normal (&lt;7 dias)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Consertos */}
      <div className="space-y-3">
        {isLoading ? (
          <Card className="p-8 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
          </Card>
        ) : consertosFilterrados.length === 0 ? (
          <Card className="p-8 text-center">
            <CheckCircle2 className="w-12 h-12 mx-auto text-green-500 mb-4" />
            <p className="text-gray-600 font-semibold">Nenhum conserto encontrado</p>
            <p className="text-sm text-gray-500">Todos os consertos foram finalizados! 🎉</p>
          </Card>
        ) : (
          consertosFilterrados.map((conserto) => (
            <Card
              key={conserto.id}
              className={`cursor-pointer hover:shadow-lg transition p-4 border-l-4 ${
                conserto.urgencia === "alta"
                  ? "border-l-red-500"
                  : conserto.urgencia === "media"
                  ? "border-l-yellow-500"
                  : "border-l-green-500"
              }`}
              onClick={() => abrirConserto(conserto.numero_pedido)}
            >
              <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                {/* Pedido */}
                <div>
                  <p className="text-xs text-gray-500 font-semibold">PEDIDO</p>
                  <p className="text-lg font-bold text-purple-600">#{conserto.numero_pedido}</p>
                </div>

                {/* Cliente */}
                <div>
                  <p className="text-xs text-gray-500 font-semibold">CLIENTE</p>
                  <p className="text-sm font-semibold">{conserto.cliente_nome}</p>
                </div>

                {/* Tipo */}
                <div>
                  <p className="text-xs text-gray-500 font-semibold">TIPO DE DEFEITO</p>
                  <p className="text-sm">{formatarTipoDefeito(conserto.tipo_defeito)}</p>
                </div>

                {/* Dias */}
                <div>
                  <p className="text-xs text-gray-500 font-semibold">DIAS</p>
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    <span className={`font-bold ${
                      conserto.dias_em_andamento > 14 ? "text-red-600" :
                      conserto.dias_em_andamento > 7 ? "text-yellow-600" :
                      "text-green-600"
                    }`}>
                      {conserto.dias_em_andamento}d
                    </span>
                  </div>
                </div>

                {/* Status */}
                <div>
                  <p className="text-xs text-gray-500 font-semibold">STATUS</p>
                  <span className={`inline-block px-2 py-1 rounded text-xs font-semibold border ${
                    STATUS_CORES[conserto.status_atual] || "bg-gray-50 border-gray-200"
                  }`}>
                    {conserto.status_atual}
                  </span>
                </div>

                {/* Prova */}
                <div>
                  <p className="text-xs text-gray-500 font-semibold">PROVA AGENDADA</p>
                  {conserto.data_prova ? (
                    <div className="flex items-center gap-1 text-sm font-semibold text-green-700">
                      <CheckCircle2 className="w-4 h-4" />
                      {conserto.data_prova}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">-</p>
                  )}
                </div>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
